import React, { useState, useEffect } from 'react';
import { db, functions } from '../../../../auth/firebase';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import SharedHeader from '../../../../components/SharedHeader';
import DashboardFooter from '../../../../components/DashboardFooter';
import { format } from 'date-fns';
import { useAuthStore } from '../../../../auth/state';
import { useNavigate } from 'react-router-dom';
import './users_control.css';
// # Import ban and suspend modals
import { BanModal, SuspendModal } from './usr_mng_utils/ban_suspend';

// # User Controls component for managing users
const UsersControl = () => {
  // # State management
  const navigate = useNavigate();
  const { isAdmin } = useAuthStore();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loginHistory, setLoginHistory] = useState([]);
  const [activeDevices, setActiveDevices] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [searchTerm, setSearchTerm] = useState('');
  // # Add state for ban and suspend modals
  const [showBanModal, setShowBanModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  // # Add state for sign out loading and feedback
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [signOutFeedback, setSignOutFeedback] = useState(null);
  // # Add state for confirmation dialog
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);

  // # Redirect if not admin
  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  // # Fetch users from Firestore
  useEffect(() => {
    const fetchUsers = async () => {
      if (!isAdmin) return;
      
      try {
        setLoading(true);
        setError(null);

        // # Get users collection reference
        const usersRef = collection(db, 'users');
        const usersSnapshot = await getDocs(usersRef);
        
        console.log('Users snapshot size:', usersSnapshot.size); // # Debug log
        
        if (usersSnapshot.empty) {
          console.log('No users found in the collection');
          setUsers([]);
          return;
        }

        // # Process user data
        const usersData = [];
        usersSnapshot.forEach(doc => {
          const data = doc.data();
          usersData.push({
            ...data,
            uid: doc.id,
            firstName: data.firstName || '',
            middleName: data.middleName || '',
            lastName: data.lastName || '',
            email: data.email || '',
            role: data.role || 'user',
            status: data.status || 'active',
            // # Include ban and suspend data
            bans: data.bans || null,
            suspends: data.suspends || null,
            metadata: {
              createdAt: data.metadata?.createdAt || data.created_at || new Date().toISOString(),
              updatedAt: data.metadata?.updatedAt || new Date().toISOString(),
              lastSignInTime: data.metadata?.lastSignInTime || new Date().toISOString()
            }
          });
        });

        console.log('Processed users:', usersData.length); // # Debug log
        setUsers(usersData);
      } catch (error) {
        console.error('Error fetching users:', error);
        setError('Failed to load users. Error: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [isAdmin]);

  // # Fetch user's login history when a user is selected
  useEffect(() => {
    const fetchLoginHistory = async () => {
      if (!selectedUser) return;
      
      setIsLoadingHistory(true);
      try {
        // # Get current month in YYYY_MM format
        const now = new Date();
        const currentMonth = `${now.getFullYear()}_${(now.getMonth() + 1).toString().padStart(2, '0')}`;
        const lastMonth = `${now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()}_${(now.getMonth() === 0 ? 12 : now.getMonth()).toString().padStart(2, '0')}`;
        
        // # Get user's activity document
        const activityDoc = await getDoc(doc(db, 'activities', selectedUser.uid));
        
        if (!activityDoc.exists()) {
          console.log('No activity found for user:', selectedUser.uid);
          setLoginHistory([]);
          return;
        }

        const data = activityDoc.data();
        const history = [];

        // # Process current and last month's data
        [currentMonth, lastMonth].forEach(month => {
          if (data[month]?.auth_history) {
            const { sign_ins = {}, sign_outs = {} } = data[month].auth_history;
            
            Object.entries(sign_ins).forEach(([id, signIn]) => {
              history.push({
                type: 'sign_in',
                ...signIn
              });
            });

            Object.entries(sign_outs).forEach(([id, signOut]) => {
              history.push({
                type: 'sign_out',
                ...signOut
              });
            });
          }
        });

        // # Sort by timestamp descending and limit to last 30 days
        history.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        const recentHistory = history.filter(item => 
          new Date(item.timestamp) > thirtyDaysAgo
        );

        setLoginHistory(recentHistory);
      } catch (error) {
        console.error('Error fetching login history:', error);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    fetchLoginHistory();
  }, [selectedUser]);

  // # Fetch user's active devices when a user is selected
  useEffect(() => {
    const fetchActiveDevices = async () => {
      if (!selectedUser) return;
      
      setIsLoadingDevices(true);
      try {
        // # Get user's activity document
        const activityDoc = await getDoc(doc(db, 'activities', selectedUser.uid));
        
        if (!activityDoc.exists()) {
          console.log('No activity found for user:', selectedUser.uid);
          setActiveDevices([]);
          return;
        }

        const data = activityDoc.data();
        
        // # Get devices from the activity document
        const devices = data.devices || {};
        
        // # Get current month in YYYY_MM format
        const now = new Date();
        const currentMonth = `${now.getFullYear()}_${(now.getMonth() + 1).toString().padStart(2, '0')}`;
        
        // # Get auth history for current month
        const authHistory = data[currentMonth]?.auth_history || { sign_ins: {}, sign_outs: {} };
        
        // # Process devices to determine which are active
        const processedDevices = Object.entries(devices).map(([key, device]) => {
          // # Find the latest sign-in for this device
          const deviceSignIns = Object.values(authHistory.sign_ins || {})
            .filter(signIn => signIn.device_id === device.device_id)
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
          
          // # Find the latest sign-out for this device
          const deviceSignOuts = Object.values(authHistory.sign_outs || {})
            .filter(signOut => signOut.device_id === device.device_id)
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
          
          // # Get latest sign-in and sign-out
          const latestSignIn = deviceSignIns[0];
          const latestSignOut = deviceSignOuts[0];
          
          // # Determine if device is active (has sign-in with no matching sign-out or sign-in is more recent than sign-out)
          const isActive = latestSignIn && (!latestSignOut || 
            new Date(latestSignIn.timestamp) > new Date(latestSignOut?.timestamp || 0));
          
          // # Get device info
          const deviceInfo = formatDeviceInfo(device.device_name);
          
          return {
            ...device,
            key,
            isActive,
            lastActive: device.last_seen,
            deviceType: deviceInfo.deviceType,
            browser: deviceInfo.browser
          };
        });
        
        setActiveDevices(processedDevices);
      } catch (error) {
        console.error('Error fetching active devices:', error);
      } finally {
        setIsLoadingDevices(false);
      }
    };

    fetchActiveDevices();
  }, [selectedUser]);

  // # Format device info for display
  const formatDeviceInfo = (device) => {
    if (!device) return { deviceType: 'Unknown Device', browser: 'Unknown Browser' };
    
    const parts = device.split(' - ');
    const platform = parts[0] || 'Unknown Platform';
    const userAgent = parts[1] || '';
    const ua = userAgent.toLowerCase();
    
    // # Enhanced device type detection
    const getDeviceType = (ua) => {
      // # Helper to extract OS version
      const getOSVersion = (regex) => {
        const match = ua.match(regex);
        return match ? match[1] : '';
      };

      // # iOS devices - specific detection with OS version
      if (/(iphone|ipod|ipad)/i.test(ua)) {
        const osVersion = getOSVersion(/cpu(?: iphone| ipad)? os (\d+_\d+(?:_\d+)?)/i)?.replace(/_/g, '.');
        
        if (/(iphone)/i.test(ua)) {
          return `iPhone (iOS ${osVersion})`;
        } else if (/(ipod)/i.test(ua)) {
          return `iPod (iOS ${osVersion})`;
        } else if (/(ipad)/i.test(ua)) {
          return `iPad (iOS ${osVersion})`;
        }
      }
      
      // # Android devices with enhanced Samsung detection
      else if (/android/i.test(ua)) {
        const androidVersion = getOSVersion(/android (\d+(?:\.\d+)?)/i);
        
        // # Samsung devices
        if (/samsung/i.test(ua)) {
          const samsungMatch = ua.match(/samsung\s+(sm-[a-z0-9]+)/i);
          if (samsungMatch) {
            const model = samsungMatch[1].toUpperCase();
            // # Convert model codes to friendly names
            const samsungModels = {
              'SM-G973U': 'Galaxy S10',
              'SM-G975U': 'Galaxy S10+',
              'SM-G970U': 'Galaxy S10e',
              'SM-G981U': 'Galaxy S20',
              'SM-G986U': 'Galaxy S20+',
              'SM-G988U': 'Galaxy S20 Ultra',
              'SM-G991U': 'Galaxy S21',
              'SM-G996U': 'Galaxy S21+',
              'SM-G998U': 'Galaxy S21 Ultra',
              // # Add more Samsung model mappings as needed
            };
            const deviceName = samsungModels[model] || model;
            return `Samsung ${deviceName} (Android ${androidVersion})`;
          }
          // # Generic Samsung device
          return `Samsung Device (Android ${androidVersion})`;
        }

        // # Other Android devices
        const modelMatch = ua.match(/android.*?;\s*(.*?)(?:build|[);])/i);
        if (modelMatch && modelMatch[1]) {
          const model = modelMatch[1]
            .trim()
            .replace(/\s+/g, ' ')
            .replace(/[_]/g, ' ')
            .split(';')[0];
          
          // # Clean up common manufacturer names
          const manufacturers = {
            'samsung': 'Samsung',
            'lg': 'LG',
            'motorola': 'Motorola',
            'huawei': 'Huawei',
            'xiaomi': 'Xiaomi',
            'oneplus': 'OnePlus',
            'google': 'Google',
            'sony': 'Sony',
            'asus': 'Asus',
            'nokia': 'Nokia',
            'oppo': 'OPPO',
            'vivo': 'Vivo',
            'realme': 'Realme'
          };
          
          for (const [key, value] of Object.entries(manufacturers)) {
            if (model.toLowerCase().includes(key)) {
              return `${value} ${model.replace(new RegExp(key, 'i'), '').trim()} (Android ${androidVersion})`;
            }
          }
          return `${model} (Android ${androidVersion})`;
        }
        
        // # Generic Android device
        return ua.includes('mobile') ? 
          `Android Mobile (${androidVersion})` : 
          `Android Tablet (${androidVersion})`;
      }
      
      // # macOS with version
      else if (/macintosh|mac os x/i.test(ua) && !/(iphone|ipad|ipod)/i.test(ua)) {
        const macVersion = getOSVersion(/mac os x (\d+[._]\d+[._]\d+)/i)?.replace(/_/g, '.');
        return macVersion ? `macOS ${macVersion}` : 'macOS';
      }
      
      // # Windows with version
      else if (/windows/i.test(ua)) {
        if (ua.includes('windows nt 10')) return 'Windows 10/11';
        if (ua.includes('windows nt 6.3')) return 'Windows 8.1';
        if (ua.includes('windows nt 6.2')) return 'Windows 8';
        if (ua.includes('windows nt 6.1')) return 'Windows 7';
        return 'Windows';
      }
      
      // # Linux (ensure it's not Android)
      else if (/linux/i.test(ua) && !/android/i.test(ua)) {
        return 'Linux';
      }
      
      return 'Unknown Device';
    };

    // # Enhanced browser detection
    const getBrowser = (ua) => {
      // # Extract browser version helper
      const getVersion = (regex) => {
        const match = ua.match(regex);
        return match ? ` ${match[1].split('.')[0]}` : '';
      };

      // # Safari detection first (as Chrome and Firefox can mask as Safari)
      if (/safari/i.test(ua) && !/chrome|chromium|edg|firefox/i.test(ua)) {
        // # Check for iOS Safari
        if (/(iphone|ipad|ipod)/i.test(ua)) {
          const version = getVersion(/version\/(\d+)/i);
          return `Mobile Safari${version}`;
        }
        const version = getVersion(/version\/(\d+)/i);
        return `Safari${version}`;
      }
      // # Edge detection
      else if (/edg/i.test(ua)) {
        const version = getVersion(/edg\/(\d+)/i);
        return `Edge${version}`;
      }
      // # Chrome detection
      else if (/chrome/i.test(ua)) {
        // # Check for Chrome iOS
        if (/(iphone|ipad|ipod)/i.test(ua)) {
          const version = getVersion(/crios\/(\d+)/i);
          return `Chrome iOS${version}`;
        }
        // # Check for Samsung Browser
        else if (/samsungbrowser/i.test(ua)) {
          const version = getVersion(/samsungbrowser\/(\d+)/i);
          return `Samsung Browser${version}`;
        }
        const version = getVersion(/chrome\/(\d+)/i);
        return `Chrome${version}`;
      }
      // # Firefox detection
      else if (/firefox/i.test(ua)) {
        // # Check for Firefox iOS
        if (/(iphone|ipad|ipod)/i.test(ua)) {
          const version = getVersion(/fxios\/(\d+)/i);
          return `Firefox iOS${version}`;
        }
        const version = getVersion(/firefox\/(\d+)/i);
        return `Firefox${version}`;
      }
      // # Opera detection
      else if (/opera|opr\//i.test(ua)) {
        const version = getVersion(/(?:opera|opr)\/(\d+)/i);
        return `Opera${version}`;
      }

      return 'Unknown Browser';
    };

    return {
      deviceType: getDeviceType(ua),
      browser: getBrowser(ua),
      platform
    };
  };

  // # Format name helper
  const formatName = (user) => {
    if (!user) return 'N/A';
    const parts = [user.firstName, user.middleName, user.lastName].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : user.email || 'N/A';
  };

  // # Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // # Format: "Mar 15, 2024 at 2:30 PM"
      return format(new Date(dateString), 'PPP p');
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // # Handle force sign out
  const handleForceSignOut = async () => {
    // # Show confirmation dialog
    setShowSignOutConfirm(true);
  };

  // # Handle confirm force sign out
  const handleConfirmForceSignOut = async () => {
    if (!selectedUser || isSigningOut) return;
    
    try {
      setIsSigningOut(true);
      setSignOutFeedback(null);
      setShowSignOutConfirm(false); // Close confirmation dialog
      
      // # Call the signOutSpecificUser function
      const signOutUser = httpsCallable(functions, 'signOutSpecificUser');
      const result = await signOutUser({ userId: selectedUser.uid });
      
      console.log('Sign out result:', result.data);
      
      // # Update active devices to show all as inactive
      setActiveDevices(prevDevices => 
        prevDevices.map(device => ({
          ...device,
          isActive: false
        }))
      );
      
      // # Show success feedback with device count if available
      const deviceCount = result.data.message.match(/(\d+) device/);
      setSignOutFeedback({
        type: 'success',
        message: deviceCount 
          ? `Signed out from ${deviceCount[1]} device(s)` 
          : 'User signed out successfully'
      });
      
      // # Refresh login history after a short delay
      setTimeout(() => {
        // # Fetch login history again to show the new sign-out records
        const fetchLoginHistory = async () => {
          setIsLoadingHistory(true);
          try {
            // # Get current month in YYYY_MM format
            const now = new Date();
            const currentMonth = `${now.getFullYear()}_${(now.getMonth() + 1).toString().padStart(2, '0')}`;
            const lastMonth = `${now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()}_${(now.getMonth() === 0 ? 12 : now.getMonth()).toString().padStart(2, '0')}`;
            
            // # Get user's activity document
            const activityDoc = await getDoc(doc(db, 'activities', selectedUser.uid));
            
            if (!activityDoc.exists()) {
              console.log('No activity found for user:', selectedUser.uid);
              setLoginHistory([]);
              return;
            }

            const data = activityDoc.data();
            const history = [];

            // # Process current and last month's data
            [currentMonth, lastMonth].forEach(month => {
              if (data[month]?.auth_history) {
                const { sign_ins = {}, sign_outs = {} } = data[month].auth_history;
                
                Object.entries(sign_ins).forEach(([id, signIn]) => {
                  history.push({
                    type: 'sign_in',
                    ...signIn
                  });
                });

                Object.entries(sign_outs).forEach(([id, signOut]) => {
                  history.push({
                    type: 'sign_out',
                    ...signOut
                  });
                });
              }
            });

            // # Sort by timestamp descending and limit to last 30 days
            history.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            
            const recentHistory = history.filter(item => 
              new Date(item.timestamp) > thirtyDaysAgo
            );

            setLoginHistory(recentHistory);
            
            // # Switch to history tab to show the sign-out records
            setActiveTab('history');
          } catch (error) {
            console.error('Error fetching login history:', error);
          } finally {
            setIsLoadingHistory(false);
          }
        };
        
        fetchLoginHistory();
      }, 1500);
      
    } catch (error) {
      console.error('Error signing out user:', error);
      setSignOutFeedback({
        type: 'error',
        message: error.message || 'Failed to sign out user'
      });
    } finally {
      setIsSigningOut(false);
    }
  };

  // # Handle ban user
  const handleBanUser = () => {
    // # Show ban modal
    setShowBanModal(true);
  };

  // # Handle ban complete
  const handleBanComplete = (isBanned) => {
    // # Update user in the list
    setUsers(prevUsers => 
      prevUsers.map(user => 
        user.uid === selectedUser.uid 
          ? { 
              ...user, 
              status: isBanned ? 'banned' : 'active',
              bans: { 
                ...user.bans,
                isBanned 
              }
            } 
          : user
      )
    );
    
    // # Update selected user
    setSelectedUser(prev => ({
      ...prev,
      status: isBanned ? 'banned' : 'active',
      bans: {
        ...prev.bans,
        isBanned
      }
    }));
  };

  // # Handle suspend user
  const handleSuspendUser = () => {
    // # Show suspend modal
    setShowSuspendModal(true);
  };

  // # Handle suspend complete
  const handleSuspendComplete = (isSuspended, suspensionEnd) => {
    // # Update user in the list
    setUsers(prevUsers => 
      prevUsers.map(user => 
        user.uid === selectedUser.uid 
          ? { 
              ...user, 
              status: isSuspended ? 'suspended' : 'active',
              suspends: { 
                ...user.suspends,
                isSuspended,
                suspensionEnd
              }
            } 
          : user
      )
    );
    
    // # Update selected user
    setSelectedUser(prev => ({
      ...prev,
      status: isSuspended ? 'suspended' : 'active',
      suspends: {
        ...prev.suspends,
        isSuspended,
        suspensionEnd
      }
    }));
  };

  // # Filter users based on search
  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    const fullName = formatName(user).toLowerCase();
    const email = user.email.toLowerCase();
    const username = (user.username || '').toLowerCase();
    
    return fullName.includes(searchLower) || 
           email.includes(searchLower) || 
           username.includes(searchLower);
  });

  // # Get user status badge class
  const getUserStatusBadge = (user) => {
    if (!user) return '';
    
    if (user.bans?.isBanned) return 'users_control_status_badge banned';
    if (user.suspends?.isSuspended) return 'users_control_status_badge suspended';
    return 'users_control_status_badge active';
  };

  // # Get user status text
  const getUserStatusText = (user) => {
    if (!user) return 'Unknown';
    
    if (user.bans?.isBanned) return 'Banned';
    if (user.suspends?.isSuspended) return 'Suspended';
    return 'Active';
  };

  return (
    <div className="users_control_container">
      <SharedHeader title="User Controls" />
      
      <div className="users_control_content">
        {loading ? (
          <div className="users_control_loading">Loading users...</div>
        ) : error ? (
          <div className="users_control_error">{error}</div>
        ) : users.length === 0 ? (
          <div className="users_control_empty">
            <h3>No users found</h3>
            <p>There might be an issue with the database connection or no users exist yet.</p>
          </div>
        ) : (
          <>
            <div className="users_control_header">
              <h2>Total Users: {users.length}</h2>
              <div className="users_control_search">
                <input
                  type="text"
                  placeholder="Search by name, email, or username..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="users_control_search_icon">🔍</span>
              </div>
            </div>
            <div className="users_control_grid">
              {filteredUsers.map(user => (
                <div 
                  key={user.uid} 
                  className="users_control_tile"
                  onClick={() => setSelectedUser(user)}
                >
                  <div className="users_control_tile_pfp">
                    <img 
                      src={user.photoURL || '/icons/pfp.png'} 
                      alt={formatName(user)}
                    />
                  </div>
                  <div className="users_control_tile_info">
                    <h3>{formatName(user)}</h3>
                    <p>{user.email}</p>
                    <span className={getUserStatusBadge(user)}>
                      {getUserStatusText(user)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* User Details Modal */}
            {selectedUser && (
              <div className="users_control_modal_overlay" onClick={() => setSelectedUser(null)}>
                <div className="users_control_modal" onClick={e => e.stopPropagation()}>
                  <button 
                    className="users_control_modal_close"
                    onClick={() => setSelectedUser(null)}
                  >
                    ×
                  </button>

                  <div className="users_control_tabs">
                    <button 
                      className={`users_control_tab ${activeTab === 'info' ? 'active' : ''}`}
                      onClick={() => setActiveTab('info')}
                    >
                      User Info
                    </button>
                    <button 
                      className={`users_control_tab ${activeTab === 'history' ? 'active' : ''}`}
                      onClick={() => setActiveTab('history')}
                    >
                      Authentication History
                    </button>
                  </div>

                  <div className="users_control_modal_content">
                    {activeTab === 'info' ? (
                      <div className="users_control_info_layout">
                        {/* Left Sidebar with Profile Picture */}
                        <div className="users_control_info_sidebar">
                          <img 
                            src={selectedUser.photoURL || '/icons/pfp.png'} 
                            alt={formatName(selectedUser)}
                            className="users_control_modal_pfp"
                          />
                          <div className={getUserStatusBadge(selectedUser)}>
                            {getUserStatusText(selectedUser)}
                          </div>
                        </div>

                        {/* Right Side Information */}
                        <div className="users_control_info_content">
                          {/* Personal Section */}
                          <section className="users_control_modal_section">
                            <h3>Personal Information</h3>
                            <div className="users_control_info_grid">
                              <div className="users_control_info_item">
                                <label>Full Name</label>
                                <p>{formatName(selectedUser)}</p>
                              </div>
                              <div className="users_control_info_item">
                                <label>Username</label>
                                <p>@{selectedUser.username || 'N/A'}</p>
                              </div>
                              <div className="users_control_info_item">
                                <label>Email</label>
                                <p>{selectedUser.email}</p>
                              </div>
                              <div className="users_control_info_item">
                                <label>Joined</label>
                                <p>{formatDate(selectedUser.metadata?.createdAt || selectedUser.created_at)}</p>
                              </div>
                            </div>
                          </section>

                          {/* Ban Information (if banned) */}
                          {selectedUser.bans?.isBanned && (
                            <section className="users_control_modal_section users_control_ban_section">
                              <h3>Ban Information</h3>
                              <div className="users_control_info_grid">
                                <div className="users_control_info_item">
                                  <label>Ban Reason</label>
                                  <p>{selectedUser.bans.banReason || 'No reason provided'}</p>
                                </div>
                                <div className="users_control_info_item">
                                  <label>Banned On</label>
                                  <p>{formatDate(selectedUser.bans.banTime?.toDate?.() || selectedUser.bans.banTime)}</p>
                                </div>
                                <div className="users_control_info_item">
                                  <label>Banned By</label>
                                  <p>{selectedUser.bans.banBy || 'Admin'}</p>
                                </div>
                              </div>
                            </section>
                          )}

                          {/* Suspension Information (if suspended) */}
                          {selectedUser.suspends?.isSuspended && (
                            <section className="users_control_modal_section users_control_suspend_section">
                              <h3>Suspension Information</h3>
                              <div className="users_control_info_grid">
                                <div className="users_control_info_item">
                                  <label>Suspension Reason</label>
                                  <p>{selectedUser.suspends.suspendReason || 'No reason provided'}</p>
                                </div>
                                <div className="users_control_info_item">
                                  <label>Suspended On</label>
                                  <p>{formatDate(selectedUser.suspends.suspendedAt?.toDate?.() || selectedUser.suspends.suspendedAt)}</p>
                                </div>
                                <div className="users_control_info_item">
                                  <label>Suspended By</label>
                                  <p>{selectedUser.suspends.suspendedBy || 'Admin'}</p>
                                </div>
                                <div className="users_control_info_item">
                                  <label>Suspension Ends</label>
                                  <p>{formatDate(selectedUser.suspends.suspensionEnd)}</p>
                                </div>
                              </div>
                            </section>
                          )}

                          {/* Active Devices Section */}
                          <section className="users_control_modal_section">
                            <h3>Active Devices</h3>
                            {isLoadingDevices ? (
                              <p>Loading devices...</p>
                            ) : activeDevices.length === 0 ? (
                              <p>No active devices found</p>
                            ) : (
                              <div className="users_control_devices_list">
                                {activeDevices.map((device, index) => (
                                  <div key={index} className="users_control_device_item">
                                    <div className={`users_control_device_status ${device.isActive ? 'active' : 'inactive'}`}>
                                      <span className="users_control_device_status_dot"></span>
                                      <span>{device.isActive ? 'Active' : 'Inactive'}</span>
                                    </div>
                                    <div className="users_control_device_info">
                                      <div className="users_control_device_main">
                                        <span className="users_control_device_type" data-device={device.deviceType}>
                                          {device.deviceType}
                                        </span>
                                        <span className="users_control_browser_type" data-browser={device.browser}>
                                          {device.browser}
                                        </span>
                                      </div>
                                      <div className="users_control_device_meta">
                                        <span>Last seen: {formatDate(device.lastActive)}</span>
                                        <span>IP: {device.ip_address}</span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </section>
                        </div>
                      </div>
                    ) : (
                      /* Authentication History Tab */
                      <div className="users_control_history_list">
                        {isLoadingHistory ? (
                          <p>Loading history...</p>
                        ) : loginHistory.length === 0 ? (
                          <p>No recent login history</p>
                        ) : (
                          loginHistory.map((activity, index) => {
                            const deviceInfo = formatDeviceInfo(activity.device);
                            const isAdminTriggered = activity.admin_triggered && activity.type === 'sign_out';
                            
                            return (
                              <div 
                                key={index} 
                                className="users_control_history_item"
                                data-type={activity.type}
                              >
                                <div className="users_control_history_icon">
                                  {activity.type === 'sign_in' ? '→' : '←'}
                                </div>
                                <div className="users_control_history_details">
                                  <div className="users_control_history_main">
                                    <span data-admin-triggered={isAdminTriggered ? 'Admin Forced' : ''}>
                                      {activity.type === 'sign_in' ? 'Sign In' : 'Sign Out'}
                                    </span>
                                    <span>
                                      <span className="users_control_device_type" data-device={deviceInfo.deviceType}>
                                        {deviceInfo.deviceType}
                                      </span>
                                      <span className="users_control_browser_type" data-browser={deviceInfo.browser}>
                                        {deviceInfo.browser}
                                      </span>
                                    </span>
                                  </div>
                                  <div className="users_control_history_meta">
                                    <span className="users_control_history_datetime">
                                      {formatDate(activity.timestamp)}
                                    </span>
                                    <span className="users_control_history_ip">
                                      IP: {activity.ip_address}
                                    </span>
                                    <span className="users_control_history_device_id">
                                      Device ID: {activity.device_id || 'N/A'}
                                    </span>
                                    <span className="users_control_history_hardware">
                                      CPU Cores: {activity.hardware_concurrency || 'N/A'}
                                    </span>
                                    <span className="users_control_history_timezone">
                                      {activity.timezone || 'N/A'}
                                    </span>
                                    {activity.forced_signout && (
                                      <span className="users_control_history_forced">
                                        Forced Sign Out
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>

                  <div className="users_control_modal_footer">
                    <button 
                      className={`users_control_action_button signout ${isSigningOut ? 'loading' : ''}`}
                      title="This will sign the user out of all devices"
                      onClick={handleForceSignOut}
                      disabled={isSigningOut}
                    >
                      {isSigningOut ? 'Signing Out...' : 'Force Sign Out'}
                    </button>
                    {signOutFeedback && (
                      <div className={`users_control_feedback ${signOutFeedback.type}`}>
                        {signOutFeedback.message}
                      </div>
                    )}
                    <button 
                      className={`users_control_action_button ${selectedUser.bans?.isBanned ? 'unban' : 'ban'}`}
                      onClick={handleBanUser}
                    >
                      {selectedUser.bans?.isBanned ? 'Unban' : 'Ban'}
                    </button>
                    <button 
                      className={`users_control_action_button ${selectedUser.suspends?.isSuspended ? 'unsuspend' : 'suspend'}`}
                      onClick={handleSuspendUser}
                    >
                      {selectedUser.suspends?.isSuspended ? 'Unsuspend' : 'Suspend'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Ban Modal */}
            {selectedUser && (
              <BanModal
                user={selectedUser}
                isOpen={showBanModal}
                onClose={() => setShowBanModal(false)}
                onBanComplete={handleBanComplete}
              />
            )}

            {/* Suspend Modal */}
            {selectedUser && (
              <SuspendModal
                user={selectedUser}
                isOpen={showSuspendModal}
                onClose={() => setShowSuspendModal(false)}
                onSuspendComplete={handleSuspendComplete}
              />
            )}

            {/* Confirmation Dialog */}
            {showSignOutConfirm && (
              <div className="users_control_modal_overlay">
                <div className="users_control_confirm_dialog">
                  <h3>Confirm Force Sign Out</h3>
                  <p>
                    Are you sure you want to force sign out {formatName(selectedUser)}? 
                    This will revoke all active sessions and sign out all devices.
                  </p>
                  <div className="users_control_confirm_actions">
                    <button 
                      className="users_control_confirm_button cancel"
                      onClick={() => setShowSignOutConfirm(false)}
                    >
                      Cancel
                    </button>
                    <button 
                      className="users_control_confirm_button confirm"
                      onClick={handleConfirmForceSignOut}
                      disabled={isSigningOut}
                    >
                      {isSigningOut ? 'Signing Out...' : 'Confirm Sign Out'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      
      <DashboardFooter />
    </div>
  );
};

export default UsersControl;
